/**
 * Ciclo de vida do QUOTE WORKER — extraído de marketdata.jsx na modularização
 * dos god-objects: criação/terminação do Web Worker de coalescing (com
 * fallback worker-less — I2), roteamento do onmessage (diagnostics, gap-log
 * com throttle 60s/símbolo, updateLastQuote + broadcast), adaptive throttling
 * por FPS (hysteresis) e o monitor de staleness por símbolo (horário B3).
 *
 * MESMO diretório do marketdata: a URL relativa do worker
 * (`../../workers/quoteWorker.js`, resolvida contra import.meta.url) continua
 * apontando pro mesmo arquivo e o Vite mantém o bundling estático do padrão
 * `new Worker(new URL(...))`.
 *
 * Estado permanece NA INSTÂNCIA (`md.quoteWorker`, `md.adaptiveThrottleInterval`,
 * `md.quoteStalenessMonitorId`, `md._diagnosticsResolve`, `md._lastGapLogAt`,
 * `md.currentQuoteThrottle`); chamadas internas via `md.<método>` (delegates).
 * Contrato pinado ANTES da extração: quoteWorkerLifecycleContract.test.js.
 * Hot path adjacente — mover, NUNCA reestruturar aqui.
 */
import logger from "../../utils/logger";

/**
 * Initialize or reinitialize the Web Worker for quote processing
 * Worker handles: rounding, OHLC accumulation, bar boundary detection, throttling
 * This offloads ~96% of quote processing work from the main thread
 */
export const initQuoteWorker = (md) => {
  // Terminate existing worker if any
  md.terminateQuoteWorker();

  try {
    // Create new worker using Vite's native worker bundling
    md.quoteWorker = new Worker(
      new URL("../../workers/quoteWorker.js", import.meta.url),
      { type: "module" },
    );

    // Handle coalesced quotes from worker
    md.quoteWorker.onmessage = (e) => {
      const coalescedQuote = e.data;

      // Handle diagnostics response
      if (coalescedQuote.type === "DIAGNOSTICS_RESULT") {
        if (md._diagnosticsResolve) {
          md._diagnosticsResolve(coalescedQuote);
          md._diagnosticsResolve = null;
        }
        return;
      }

      // Trade gap is high-frequency on active symbols (e.g. WINFUT). Keep it OUT of the
      // operational Log panel (diagnostic = silent) and throttle to 1×/60s per symbol so
      // it doesn't flood the console or machine-gun the remote log batcher. Still recorded
      // (diagnostic) when remote shipping/REC is on.
      if (coalescedQuote._gapDetected) {
        const gapSym = coalescedQuote.S;
        const nowMs = Date.now();
        if (!md._lastGapLogAt) md._lastGapLogAt = {};
        if (nowMs - (md._lastGapLogAt[gapSym] || 0) > 60000) {
          md._lastGapLogAt[gapSym] = nowMs;
          console.warn(
            `[Marketdata] ⚠️ TRADE GAP: ${gapSym} — missed ${coalescedQuote._missedTrades} trades ` +
              `(expected ON=${coalescedQuote._expectedON}, got PON=${coalescedQuote._gotPON})`,
          );
          logger.diagnostic.warning(`Trade gap: ${gapSym}`, {
            symbol: gapSym,
            missedTrades: coalescedQuote._missedTrades,
          });
        }
      }

      // Update synthesized last quote (only ~4/sec per symbol, not 100+)
      md.updateLastQuote(coalescedQuote);

      // Broadcast to React/Zustand stores
      md.broadcastQuoteToReceivers(coalescedQuote);
    };

    // Handle worker errors
    md.quoteWorker.onerror = (error) => {
      console.error("[Marketdata] Quote worker error:", error.message);
      console.error("[Marketdata] Worker error details:", {
        filename: error.filename,
        lineno: error.lineno,
        colno: error.colno,
      });
      // Don't terminate - worker may still function for other quotes
    };

    console.log(
      `[${new Date().toISOString()}] 🔧 Quote Web Worker initialized`,
    );

    // Start adaptive throttling based on FPS
    md.startAdaptiveThrottling();
  } catch (error) {
    console.error("[Marketdata] Failed to initialize quote worker:", error);
    // Fallback: worker-less mode (quotes processed on main thread)
    md.quoteWorker = null;
  }
};

/**
 * Safely terminate the quote worker
 */
export const terminateQuoteWorker = (md) => {
  // Stop adaptive throttling first
  md.stopAdaptiveThrottling();

  if (md.quoteWorker) {
    try {
      md.quoteWorker.terminate();
      console.log(
        `[${new Date().toISOString()}] 🛑 Quote Web Worker terminated`,
      );
    } catch (error) {
      console.error("[Marketdata] Error terminating quote worker:", error);
    }
    md.quoteWorker = null;
  }
};

/**
 * Start adaptive throttling based on FPS
 * Monitors FPS every 2 seconds and adjusts worker throttle interval:
 * - FPS ≥45: 250ms throttle (4 updates/sec) - normal performance
 * - FPS 30-44: 500ms throttle (2 updates/sec) - help recovery
 * - FPS <30: 1000ms throttle (1 update/sec) - severe load
 *
 * Uses hysteresis to prevent thrashing:
 * - Increase throttle after 2 consecutive low FPS checks
 * - Decrease throttle after 3 consecutive high FPS checks
 * - Minimum 10 seconds at each level before changing
 */
export const startAdaptiveThrottling = (md) => {
  // Track state for hysteresis to prevent thrashing
  let lowFpsCount = 0;
  let highFpsCount = 0;
  let currentThrottle = 250;
  let lastChangeTime = Date.now();
  const MIN_DURATION_MS = 30000; // Minimum 30 seconds at each level

  md.adaptiveThrottleInterval = setInterval(() => {
    if (!md.quoteWorker) return;

    // Get current FPS from global performance monitor
    const snapshot = window.perfMon?._getSnapshot?.();
    if (!snapshot) return;

    const fps = parseFloat(snapshot.fps) || 60;
    const now = Date.now();
    const timeSinceLastChange = now - lastChangeTime;
    let targetThrottle = currentThrottle;

    // Determine target based on FPS with hysteresis
    if (fps < 30) {
      // Severe load - need aggressive throttling
      lowFpsCount++;
      highFpsCount = 0;
      if (lowFpsCount >= 2) {
        targetThrottle = 1000; // 1 update/sec
      }
    } else if (fps < 45) {
      // Moderate load - reduce updates
      lowFpsCount++;
      highFpsCount = 0;
      if (lowFpsCount >= 2) {
        targetThrottle = 500; // 2 updates/sec
      }
    } else {
      // Good performance - can recover
      lowFpsCount = 0;
      highFpsCount++;
      // Recovery: need sustained good FPS (10 consecutive checks)
      if (highFpsCount >= 10) {
        if (fps >= 50 && currentThrottle > 250) {
          targetThrottle = 250; // Full recovery
        } else if (fps >= 45 && currentThrottle > 500) {
          targetThrottle = 500; // Partial recovery
        }
      }
    }

    // Apply change if different AND minimum duration has passed
    if (targetThrottle !== currentThrottle) {
      // Always allow increasing throttle (degrading) immediately after min duration
      // But require min duration before recovering (decreasing throttle)
      const isIncreasing = targetThrottle > currentThrottle;
      const canChange = timeSinceLastChange >= MIN_DURATION_MS;

      if (canChange || (isIncreasing && lowFpsCount >= 3)) {
        console.log(
          `[${new Date().toISOString()}] 📊 Adaptive throttle: FPS=${fps.toFixed(1)}, ${currentThrottle}ms → ${targetThrottle}ms (after ${(timeSinceLastChange / 1000).toFixed(1)}s)`,
        );
        currentThrottle = targetThrottle;
        lastChangeTime = now;
        // Reset counters after change to prevent immediate re-triggering
        lowFpsCount = 0;
        highFpsCount = 0;
        md.currentQuoteThrottle = targetThrottle; // Expose for debug overlay
        md.quoteWorker.postMessage({
          type: "SET_THROTTLE",
          interval: targetThrottle,
        });
      }
    }
  }, 2000); // Check every 2 seconds

  console.log(
    `[${new Date().toISOString()}] 📊 Adaptive quote throttling started`,
  );
};

/**
 * Stop adaptive throttling
 */
export const stopAdaptiveThrottling = (md) => {
  if (md.adaptiveThrottleInterval) {
    clearInterval(md.adaptiveThrottleInterval);
    md.adaptiveThrottleInterval = null;
    console.log(
      `[${new Date().toISOString()}] 📊 Adaptive quote throttling stopped`,
    );
  }
};

/**
 * Start periodic per-symbol quote staleness monitor.
 * Detects when a subscribed symbol stops receiving quotes (single-symbol freeze).
 * Only warns during B3 market hours (10:00-17:30 BRT, weekdays).
 */
export const startQuoteStalenessMonitor = (md) => {
  md.stopQuoteStalenessMonitor();

  md.quoteStalenessMonitorId = setInterval(() => {
    if (md.state !== "online") return;

    // Only check during B3 market hours (BRT = UTC-3)
    const now = new Date();
    const brt = new Date(now.getTime() - 3 * 60 * 60 * 1000);
    const hour = brt.getUTCHours();
    const minute = brt.getUTCMinutes();
    const day = brt.getUTCDay();
    const timeInMinutes = hour * 60 + minute;

    // Weekdays 10:00-17:30 BRT
    if (day === 0 || day === 6) return; // Weekend
    if (timeInMinutes < 600 || timeInMinutes > 1050) return; // Outside 10:00-17:30

    const nowMs = Date.now();
    const STALE_THRESHOLD_MS = 60000; // 60 seconds
    const subscriptions = md.subscriptionManager.debug();

    for (const sub of subscriptions) {
      const symbol = sub.canonical || sub.symbol;
      const receivedAt = md.lastQuotes.get(symbol)?._receivedAt;

      if (!receivedAt) continue; // Never received a quote yet — skip (still loading)

      const age = nowMs - receivedAt;
      if (age > STALE_THRESHOLD_MS) {
        console.warn(
          `[QuoteStaleness] ⚠️ ${symbol} — no quote for ${Math.round(age / 1000)}s ` +
            `(subscribers: ${sub.refCount}, last: ${new Date(receivedAt).toLocaleTimeString()})`,
        );
        // diagnostic channel: emits to the console + remote shipping
        // (MongoDB when REC is on) but is kept OUT of the operational
        // Log panel — a flat 60s threshold false-positives on illiquid
        // symbols (JBSS32 BDR, DI1FUT continuous) and would flood it.
        logger.diagnostic.warning(`Quote stale: ${symbol}`, {
          symbol,
          staleSec: Math.round(age / 1000),
          subscribers: sub.refCount,
        });
      }
    }
  }, 30000); // Check every 30 seconds

  console.log(
    `[${new Date().toISOString()}] 🔍 Quote staleness monitor started`,
  );
};

/**
 * Stop the per-symbol quote staleness monitor
 */
export const stopQuoteStalenessMonitor = (md) => {
  if (md.quoteStalenessMonitorId) {
    clearInterval(md.quoteStalenessMonitorId);
    md.quoteStalenessMonitorId = null;
  }
};

/**
 * Debug: Get worker internal state (pending quotes, broadcast times, daily cache).
 * Returns a Promise that resolves with worker diagnostics.
 */
export const debugWorkerState = (md) => {
  if (!md.quoteWorker) {
    console.log("[Marketdata] No quote worker active");
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    md._diagnosticsResolve = resolve;
    md.quoteWorker.postMessage({ type: "DIAGNOSTICS" });

    // Timeout after 2 seconds
    setTimeout(() => {
      if (md._diagnosticsResolve) {
        md._diagnosticsResolve = null;
        resolve(null);
        console.warn("[Marketdata] Worker diagnostics timed out");
      }
    }, 2000);
  });
};
